import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Loader2,
  Power,
  ShieldCheck,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { AdminUserBulkDeletionDialog } from "@/components/AdminUserBulkDeletionDialog";

const REGIONS = [
  "Jakarta Pusat",
  "Jakarta Utara",
  "Jakarta Barat",
  "Jakarta Selatan",
  "Jakarta Timur",
  "Kepulauan Seribu",
];

const INPUT_CLASS = (
  "w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm "
  + "focus:outline-none focus:ring-2 focus:ring-[#27AE60]"
);

function emptyForm(canManageProvincial) {
  return {
    name: "",
    email: "",
    password: "",
    role: canManageProvincial ? "admin" : "admin_wilayah",
    region: "",
  };
}

function roleLabel(role) {
  if (role === "super_admin") return "Super Admin";
  if (role === "admin_wilayah") return "Admin Wilayah";
  if (role === "interviewer") return "Pewawancara";
  return "Admin Provinsi";
}

const REGION_SCOPED_ROLES = ["admin_wilayah", "interviewer"];

const ADMIN_USER_COLLATOR = new Intl.Collator("id", { numeric: true, sensitivity: "base" });

function AdminUserSortableHeader({ column, label, onSort, sortConfig }) {
  const isActive = sortConfig.key === column;
  const direction = isActive ? sortConfig.direction : null;
  const nextAction = direction === "asc" ? "Urutkan menurun" : "Urutkan menaik";
  const Icon = direction === "asc" ? ChevronUp : direction === "desc" ? ChevronDown : ChevronsUpDown;

  return (
    <th className="px-4 py-3 font-semibold">
      <button
        type="button"
        onClick={() => onSort(column)}
        data-testid={`sort-admin-users-${column}`}
        aria-label={`${nextAction} berdasarkan ${label}`}
        title={`${nextAction} berdasarkan ${label}`}
        className={[
          "inline-flex items-center gap-1.5 transition-colors",
          isActive ? "text-[#0B6B3A]" : "hover:text-[#1F2937]",
        ].join(" ")}
      >
        {label}
        <Icon className="h-3.5 w-3.5" />
      </button>
    </th>
  );
}

function adminUserSortValue(account, key) {
  if (key === "email") return account.email || "";
  if (key === "role") return roleLabel(account.role);
  if (key === "region") return account.region || "Provinsi DKI";
  if (key === "status") return account.is_active ? "Aktif" : "Nonaktif";
  return account.name || "";
}

export default function AdminUsers({ canManageProvincial = false }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(() => emptyForm(canManageProvincial));
  const [saving, setSaving] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkDeletion, setBulkDeletion] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const roleOptions = useMemo(() => (
    canManageProvincial
      ? [
        ["admin", "Admin Provinsi"],
        ["admin_wilayah", "Admin Wilayah"],
        ["interviewer", "Pewawancara"],
        ["super_admin", "Super Admin"],
      ]
      : [
        ["admin_wilayah", "Admin Wilayah"],
        ["interviewer", "Pewawancara"],
      ]
  ), [canManageProvincial]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { role: "all" } });
      setUsers(data);
      setSelectedUserIds((current) => {
        const availableIds = new Set(data.map((account) => account.user_id));
        return current.filter((userId) => availableIds.has(userId));
      });
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return users;
    return [...users].sort((first, second) => {
      const firstValue = adminUserSortValue(first, sortConfig.key);
      const secondValue = adminUserSortValue(second, sortConfig.key);
      const comparison = ADMIN_USER_COLLATOR.compare(String(firstValue), String(secondValue));
      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [sortConfig, users]);

  const selectableUsers = useMemo(
    () => sortedUsers.filter((account) => account.role !== "super_admin"),
    [sortedUsers],
  );
  const allSelectableUsersSelected = (
    selectableUsers.length > 0
    && selectableUsers.every((account) => selectedUserIds.includes(account.user_id))
  );

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((current) => (
      current.includes(userId)
        ? current.filter((selectedUserId) => selectedUserId !== userId)
        : [...current, userId]
    ));
  };

  const toggleAllUserSelections = () => {
    setSelectedUserIds((current) => {
      if (allSelectableUsersSelected) {
        const selectableIds = new Set(selectableUsers.map((account) => account.user_id));
        return current.filter((userId) => !selectableIds.has(userId));
      }
      const combinedIds = new Set(current);
      selectableUsers.forEach((account) => combinedIds.add(account.user_id));
      return [...combinedIds];
    });
  };

  const updateSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const create = async (event) => {
    event.preventDefault();
    if (form.password.length < 8) {
      toast.error("Kata sandi minimal 8 karakter.");
      return;
    }
    if (REGION_SCOPED_ROLES.includes(form.role) && !form.region) {
      toast.error("Pilih wilayah kerja untuk role ini.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Akun admin berhasil dibuat.");
      setShowCreate(false);
      setForm(emptyForm(canManageProvincial));
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (account) => {
    try {
      await api.put(`/admin/users/${account.user_id}`, { is_active: !account.is_active });
      toast.success("Status akun diperbarui.");
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const remove = async (account) => {
    if (!window.confirm(`Hapus akun ${account.name}?`)) return;
    try {
      await api.delete(`/admin/users/${account.user_id}`);
      toast.success("Akun dihapus.");
      setSelectedUserIds((current) => current.filter((userId) => userId !== account.user_id));
      await load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-users-manager">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <p className="text-sm text-[#6B7280]" data-testid="admin-user-total">
          {users.length} akun pengelola
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setBulkDeletion({ scope: "all", userIds: [] })}
            disabled={selectableUsers.length === 0}
            data-testid="open-delete-all-admin-users"
            className="flex items-center gap-2 rounded-xl border border-[#FECACA] bg-white px-4 py-2.5 text-sm font-bold text-[#DC2626] transition-colors hover:bg-[#FEF2F2] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" /> Hapus Semua
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            data-testid="create-admin-btn"
            className="flex items-center gap-2 rounded-xl bg-[#27AE60] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0B6B3A]"
          >
            <UserPlus className="h-4 w-4" />
            Buat Akun Admin
          </button>
        </div>
      </div>

      {selectedUserIds.length > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 border border-[#FECACA] bg-[#FEF2F2] px-4 py-3"
          data-testid="selected-admin-users-action-bar"
        >
          <p className="text-sm font-bold text-[#991B1B]" data-testid="selected-admin-users-count">
            {selectedUserIds.length} akun pengelola dipilih
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelectedUserIds([])}
              data-testid="clear-admin-user-selection"
              className="rounded-lg px-3 py-2 text-xs font-bold text-[#991B1B] transition-colors hover:bg-[#FECACA]"
            >
              Batalkan Pilihan
            </button>
            <button
              type="button"
              onClick={() => setBulkDeletion({ scope: "selected", userIds: selectedUserIds })}
              data-testid="open-selected-admin-user-deletion"
              className="inline-flex items-center gap-2 rounded-lg bg-[#DC2626] px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-[#B91C1C]"
            >
              <Trash2 className="h-4 w-4" /> Hapus Akun Terpilih
            </button>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto mdj-scrollbar">
          <table className="w-full text-sm" data-testid="admins-table">
            <thead>
              <tr className="border-b border-gray-100 bg-[#F9FAFB] text-left text-xs text-[#6B7280]">
                <th className="w-12 px-3 py-3">
                  <input
                    type="checkbox"
                    checked={allSelectableUsersSelected}
                    onChange={toggleAllUserSelections}
                    disabled={selectableUsers.length === 0}
                    data-testid="select-all-admin-users"
                    aria-label="Pilih semua akun pengelola yang dapat dihapus"
                    className="h-4 w-4 accent-[#0B6B3A] disabled:cursor-not-allowed"
                  />
                </th>
                <AdminUserSortableHeader column="name" label="Nama" onSort={updateSort} sortConfig={sortConfig} />
                <AdminUserSortableHeader column="email" label="Email" onSort={updateSort} sortConfig={sortConfig} />
                <AdminUserSortableHeader column="role" label="Peran" onSort={updateSort} sortConfig={sortConfig} />
                <AdminUserSortableHeader column="region" label="Wilayah" onSort={updateSort} sortConfig={sortConfig} />
                <AdminUserSortableHeader column="status" label="Status" onSort={updateSort} sortConfig={sortConfig} />
                <th className="px-4 py-3 text-right font-semibold">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                    <Loader2 className="inline h-5 w-5 animate-spin" />
                  </td>
                </tr>
              ) : sortedUsers.map((account) => (
                <tr key={account.user_id} className="border-b border-gray-50 hover:bg-[#F9FAFB]">
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(account.user_id)}
                      disabled={account.role === "super_admin"}
                      onChange={() => toggleUserSelection(account.user_id)}
                      data-testid={`select-admin-user-${account.user_id}`}
                      aria-label={`Pilih ${account.name}`}
                      title={account.role === "super_admin" ? "Akun Super Admin tidak dapat dipilih" : `Pilih ${account.name}`}
                      className="h-4 w-4 accent-[#0B6B3A] disabled:cursor-not-allowed disabled:opacity-40"
                    />
                  </td>
                  <td className="px-4 py-3 font-semibold text-[#1F2937]">{account.name}</td>
                  <td className="px-4 py-3 text-[#6B7280]">{account.email}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#E8F6EE] px-2.5 py-1 text-xs font-bold text-[#0B6B3A]">
                      <ShieldCheck className="h-3 w-3" />
                      {roleLabel(account.role)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#6B7280]">{account.region || "Provinsi DKI"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${account.is_active ? "bg-[#E8F6EE] text-[#0B6B3A]" : "bg-[#FEE2E2] text-[#DC2626]"}`}>
                      {account.is_active ? "Aktif" : "Nonaktif"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {account.role !== "super_admin" && (
                      <div className="inline-flex gap-1.5">
                        <button
                          type="button"
                          onClick={() => toggle(account)}
                          data-testid={`toggle-user-${account.user_id}`}
                          className="rounded-lg p-2 text-[#6B7280] hover:bg-gray-100"
                          aria-label="Aktif atau nonaktifkan akun"
                        >
                          <Power className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(account)}
                          data-testid={`delete-user-${account.user_id}`}
                          className="rounded-lg p-2 text-[#DC2626] hover:bg-[#FEE2E2]"
                          aria-label="Hapus akun"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#1F2937]/50 p-4"
          onClick={() => setShowCreate(false)}
          data-testid="create-admin-overlay"
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6"
            onClick={(event) => event.stopPropagation()}
            data-testid="create-admin-modal"
          >
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-[#1F2937]">
                Buat Akun Admin
              </h3>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                data-testid="close-create-admin-modal"
                className="rounded-lg p-2 hover:bg-gray-100"
                aria-label="Tutup formulir akun admin"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <Field label="Nama Lengkap">
                <input
                  value={form.name}
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    name: event.target.value,
                  }))}
                  required
                  data-testid="admin-name-input"
                  className={INPUT_CLASS}
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    email: event.target.value,
                  }))}
                  required
                  data-testid="admin-email-input"
                  className={INPUT_CLASS}
                />
              </Field>
              <Field label="Kata Sandi">
                <input
                  type="text"
                  value={form.password}
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    password: event.target.value,
                  }))}
                  required
                  placeholder="Min. 8 karakter"
                  data-testid="admin-password-input"
                  className={INPUT_CLASS}
                />
              </Field>
              <Field label="Peran">
                <select
                  value={form.role}
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    role: event.target.value,
                    region: REGION_SCOPED_ROLES.includes(event.target.value) ? current.region : "",
                  }))}
                  data-testid="admin-role-select"
                  className={`${INPUT_CLASS} bg-white`}
                >
                  {roleOptions.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </Field>
              {REGION_SCOPED_ROLES.includes(form.role) && (
                <Field label="Wilayah Kerja">
                  <select
                    value={form.region}
                    onChange={(event) => setForm((current) => ({
                      ...current,
                      region: event.target.value,
                    }))}
                    required
                    data-testid="admin-region-select"
                    className={`${INPUT_CLASS} bg-white`}
                  >
                    <option value="">Pilih wilayah</option>
                    {REGIONS.map((region) => <option key={region} value={region}>{region}</option>)}
                  </select>
                </Field>
              )}
              <button
                type="submit"
                disabled={saving}
                data-testid="submit-admin-btn"
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#27AE60] py-2.5 font-bold text-white transition-colors hover:bg-[#0B6B3A] disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Buat Akun
              </button>
            </form>
          </div>
        </div>
      )}

      {bulkDeletion && (
        <AdminUserBulkDeletionDialog
          target={bulkDeletion}
          onClose={() => setBulkDeletion(null)}
          onDeleted={() => {
            setSelectedUserIds([]);
            load();
          }}
        />
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-semibold text-[#1F2937]">{label}</label>
      {children}
    </div>
  );
}
